import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  ArrowUpRight,
  GraduationCap,
  MapPin,
  Phone,
  Mail,
  Globe,
  Heart,
  ExternalLink,
  Sparkles,
  BookOpen,
  ShieldCheck,
} from "lucide-react";

import {
  FaFacebookF,
  FaYoutube,
  FaLinkedinIn,
  FaInstagram,
} from "react-icons/fa";

import logo from "/assets/img/bubbanlogo.jpg";

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

const item = {
  hidden: { opacity: 0, y: 25 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, ease: "easeOut" },
  },
};

const footerGroups = [
  {
    title: "Explore",
    icon: Globe,
    links: [
      { label: "Home", to: "/" },
      { label: "About Us", to: "/about" },
      { label: "Academic Programs", to: "/programs" },
      { label: "Fees & Payments", to: "/fees" },
      {
        label: "Certificate Verification",
        to: "/certificate-verification",
      },
    ],
  },
  {
    title: "Resources",
    icon: BookOpen,
    links: [
      {
        label: "University Library",
        to: "https://library.bubban.ac.bd",
        external: true,
      },
      {
        label: "Student Portal",
        to: "https://web.bubban.ac.bd/login",
        external: true,
      },
      { label: "Our Faculty", to: "/faculty" },
      { label: "Career Opportunities", to: "/career" },
      { label: "Videos & Media", to: "/videos" },
    ],
  },
  {
    title: "University Updates",
    icon: GraduationCap,
    links: [
      { label: "Latest Notices", to: "/all-notice" },
      { label: "University News", to: "/all-news" },
      { label: "Admission Instructions", to: "/admission-instruction" },
      {
        label: "Admission Advertisement",
        to: "/admission-advertisements",
      },
      { label: "Scholarships", to: "/scholarships" },
    ],
  },
];

const socialLinks = [
  {
    label: "Facebook",
    icon: FaFacebookF,
    url: "https://www.facebook.com/bubban.ac.bd",
    hover: "hover:bg-[#1877F2]",
  },
  {
    label: "YouTube",
    icon: FaYoutube,
    url: "https://www.youtube.com/@BandarbanUniversity",
    hover: "hover:bg-[#FF0000]",
  },
  {
    label: "LinkedIn",
    icon: FaLinkedinIn,
    url: "https://www.linkedin.com/",
    hover: "hover:bg-[#0A66C2]",
  },
  {
    label: "Instagram",
    icon: FaInstagram,
    url: "https://www.instagram.com/",
    hover: "hover:bg-gradient-to-br hover:from-purple-600 hover:to-pink-500",
  },
];

export default function Footer() {
  const year = new Date().getFullYear();

  return (
    <footer className="relative isolate overflow-hidden bg-[#061b12] text-white">

      {/* ================= BACKGROUND EFFECTS ================= */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-green-950 via-[#062b1b] to-[#04150e]" />

        <motion.div
          animate={{
            x: [0, 35, 0],
            y: [0, -25, 0],
            scale: [1, 1.12, 1],
          }}
          transition={{
            duration: 14,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          className="absolute -left-40 top-0 h-96 w-96 rounded-full bg-green-500/15 blur-[100px]"
        />

        <motion.div
          animate={{
            x: [0, -30, 0],
            y: [0, 25, 0],
          }}
          transition={{
            duration: 16,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          className="absolute -right-32 top-40 h-96 w-96 rounded-full bg-emerald-400/10 blur-[100px]"
        />

        <div
          className="absolute inset-0 opacity-[0.035]"
          style={{
            backgroundImage:
              "radial-gradient(#ffffff 1px, transparent 1px)",
            backgroundSize: "26px 26px",
          }}
        />
      </div>

      {/* ================= TOP CTA BANNER ================= */}
      <div className="relative mx-auto max-w-7xl px-4 pt-10 sm:px-6 lg:px-8 lg:pt-14">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.65 }}
          className="relative overflow-hidden rounded-[2rem] border border-white/15 bg-gradient-to-r from-green-800 via-green-700 to-emerald-600 p-6 shadow-2xl shadow-green-950/30 sm:p-9 lg:p-11"
        >
          {/* Banner decoration */}
          <motion.div
            animate={{ rotate: 360 }}
            transition={{
              duration: 45,
              repeat: Infinity,
              ease: "linear",
            }}
            className="pointer-events-none absolute -right-16 -top-24 h-72 w-72 rounded-full border border-white/10"
          />

          <div className="pointer-events-none absolute -right-12 -bottom-28 h-72 w-72 rounded-full bg-emerald-200/15 blur-3xl" />

          <div className="relative flex flex-col items-start justify-between gap-7 md:flex-row md:items-center">
            <div className="max-w-2xl">
              <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1.5 text-xs font-bold uppercase tracking-[0.16em] text-emerald-100">
                <Sparkles size={14} />
                Your Future Starts Here
              </span>

              <h2 className="mt-4 text-2xl font-black leading-tight tracking-tight sm:text-3xl lg:text-4xl">
                Build Your Future With
                <span className="block text-emerald-200">
                  Bandarban University
                </span>
              </h2>

              <p className="mt-3 max-w-xl text-sm leading-7 text-green-50/85 sm:text-base">
                Discover new opportunities, pursue your ambitions, and
                take the next step in your academic journey.
              </p>
            </div>

            <Link
              to="/apply-online"
              className="group inline-flex shrink-0 items-center gap-3 rounded-2xl bg-white px-6 py-4 text-sm font-extrabold text-green-900 shadow-xl transition-all duration-300 hover:-translate-y-1 hover:bg-emerald-50 hover:shadow-2xl"
            >
              Apply for Admission

              <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-green-100 transition-colors group-hover:bg-green-800 group-hover:text-white">
                <ArrowRight
                  size={17}
                  className="transition-transform group-hover:translate-x-0.5"
                />
              </span>
            </Link>
          </div>
        </motion.div>
      </div>

      {/* ================= BRAND & SOCIAL SECTION ================= */}
      <motion.div
        variants={container}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.15 }}
        className="relative mx-auto grid max-w-7xl grid-cols-1 gap-10 px-4 py-12 sm:px-6 md:grid-cols-2 md:py-16 lg:grid-cols-4 lg:gap-12 lg:px-8"
      >

        {/* University identity */}
        <motion.div variants={item} className="lg:col-span-1">
          <Link to="/" className="group inline-flex items-center gap-3">
            <motion.div
              whileHover={{ rotate: -5, scale: 1.06 }}
              transition={{ type: "spring", stiffness: 300 }}
              className="relative flex h-[68px] w-[68px] shrink-0 items-center justify-center rounded-2xl bg-white p-2 shadow-xl shadow-black/20"
            >
              <img
                src={logo}
                alt="Bandarban University logo"
                className="h-full w-full object-contain"
              />

              <span className="absolute -right-1 -top-1 h-4 w-4 rounded-full border-2 border-[#062b1b] bg-emerald-400" />
            </motion.div>

            <div>
              <h3 className="text-lg font-black leading-tight text-white transition-colors group-hover:text-emerald-300 sm:text-xl">
                Bandarban
              </h3>
              <p className="mt-1 text-xs font-bold uppercase tracking-[0.18em] text-emerald-300">
                University
              </p>
            </div>
          </Link>

          <p className="mt-6 max-w-sm text-sm leading-7 text-green-100/65">
            Empowering minds through quality education, innovation,
            research, and a commitment to a brighter future.
          </p>

          {/* Social links */}
          <div className="mt-6">
            <h4 className="mb-3 text-xs font-bold uppercase tracking-[0.16em] text-emerald-300">
              Connect With Us
            </h4>

            <div className="flex flex-wrap gap-3">
              {socialLinks.map((social, index) => {
                const Icon = social.icon;

                return (
                  <motion.a
                    key={social.label}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={social.label}
                    title={social.label}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.08 }}
                    whileHover={{ y: -5, scale: 1.08 }}
                    whileTap={{ scale: 0.94 }}
                    className={`flex h-11 w-11 items-center justify-center rounded-xl border border-white/10 bg-white/[0.07] text-white transition-all duration-300 hover:border-transparent hover:text-white ${social.hover}`}
                  >
                    <Icon size={17} />
                  </motion.a>
                );
              })}
            </div>
          </div>
        </motion.div>

        {/* Navigation columns */}
        {footerGroups.map((group) => {
          const GroupIcon = group.icon;

          return (
            <motion.div key={group.title} variants={item}>
              <div className="mb-6 flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl border border-emerald-400/15 bg-emerald-400/10 text-emerald-300">
                  <GroupIcon size={19} />
                </span>

                <div>
                  <h4 className="font-extrabold text-white">
                    {group.title}
                  </h4>
                  <div className="mt-2 h-0.5 w-9 rounded-full bg-gradient-to-r from-emerald-400 to-transparent" />
                </div>
              </div>

              <ul className="space-y-3">
                {group.links.map((link) => {
                  const linkClasses =
                    "group inline-flex items-start gap-2 text-sm leading-6 text-green-100/65 transition-colors duration-300 hover:text-emerald-300";

                  const content = (
                    <>
                      <ArrowRight
                        size={14}
                        className="mt-1 shrink-0 text-emerald-500/70 transition-transform duration-300 group-hover:translate-x-1 group-hover:text-emerald-300"
                      />

                      <span>{link.label}</span>

                      {link.external && (
                        <ExternalLink
                          size={12}
                          className="mt-1 shrink-0 opacity-0 transition-opacity group-hover:opacity-100"
                        />
                      )}
                    </>
                  );

                  return (
                    <li key={link.label}>
                      {link.external ? (
                        <a
                          href={link.to}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={linkClasses}
                        >
                          {content}
                        </a>
                      ) : (
                        <Link to={link.to} className={linkClasses}>
                          {content}
                        </Link>
                      )}
                    </li>
                  );
                })}
              </ul>
            </motion.div>
          );
        })}
      </motion.div>

      {/* ================= CONTACT STRIP ================= */}
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="grid grid-cols-1 overflow-hidden rounded-2xl border border-white/10 bg-white/[0.045] backdrop-blur-md sm:grid-cols-2 lg:grid-cols-3"
        >
          {/* Phone */}
          <a
            href="tel:+8801318305900"
            className="group flex items-start gap-4 border-b border-white/10 p-5 transition-colors hover:bg-white/[0.06] sm:border-r lg:border-b-0"
          >
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-emerald-400/10 text-emerald-300 transition-all duration-300 group-hover:bg-emerald-400 group-hover:text-green-950">
              <Phone size={20} />
            </span>

            <div className="min-w-0">
              <p className="text-xs font-bold uppercase tracking-[0.12em] text-emerald-300">
                Call Us
              </p>

              <p className="mt-2 break-words text-sm font-semibold text-white">
                +880 1318-305900
              </p>

              <p className="mt-1 text-sm text-green-100/60">
                +880 1318-305899
              </p>
            </div>
          </a>

          {/* Email */}
          <a
            href="mailto:info@bubban.ac.bd"
            className="group flex items-start gap-4 border-b border-white/10 p-5 transition-colors hover:bg-white/[0.06] lg:border-b-0 lg:border-r"
          >
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-emerald-400/10 text-emerald-300 transition-all duration-300 group-hover:bg-emerald-400 group-hover:text-green-950">
              <Mail size={20} />
            </span>

            <div className="min-w-0">
              <p className="text-xs font-bold uppercase tracking-[0.12em] text-emerald-300">
                Email Us
              </p>

              <p className="mt-2 break-words text-sm font-semibold text-white">
                info@bubban.ac.bd
              </p>

              <p className="mt-1 text-xs text-green-100/60">
                We welcome your inquiries.
              </p>
            </div>
          </a>

          {/* Address */}
          <a
            href="https://www.google.com/maps/search/?api=1&query=K.B+Road%2C+Shualok%2C+Bandarban%2C+Bangladesh"
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-start gap-4 p-5 transition-colors hover:bg-white/[0.06] sm:col-span-2 lg:col-span-1"
          >
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-emerald-400/10 text-emerald-300 transition-all duration-300 group-hover:bg-emerald-400 group-hover:text-green-950">
              <MapPin size={20} />
            </span>

            <div className="min-w-0">
              <p className="text-xs font-bold uppercase tracking-[0.12em] text-emerald-300">
                Visit Us
              </p>

              <p className="mt-2 text-sm font-semibold leading-6 text-white">
                K.B Road, Shualok
              </p>

              <p className="mt-1 text-xs leading-5 text-green-100/60">
                Post Office: Shualok - 4600, Bandarban Sadar, Bangladesh
              </p>
            </div>
          </a>
        </motion.div>
      </div>

      {/* ================= COPYRIGHT ================= */}
      <div className="relative mt-12 border-t border-white/10">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mx-auto flex max-w-7xl flex-col gap-5 px-4 py-6 text-sm sm:px-6 md:flex-row md:items-center md:justify-between lg:px-8"
        >
          <p className="text-center leading-6 text-green-100/60 md:text-left">
            © {year}{" "}
            <span className="font-bold text-white">
              Bandarban University
            </span>
            . All Rights Reserved.
          </p>

          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-3">
            <Link
              to="/privacy-policy"
              className="inline-flex items-center gap-2 text-green-100/60 transition hover:text-emerald-300"
            >
              <ShieldCheck size={15} />
              Privacy Policy
            </Link>

            <Link
              to="/legal"
              className="text-green-100/60 transition hover:text-emerald-300"
            >
              Legal
            </Link>

            <span className="hidden h-4 w-px bg-white/15 sm:block" />

            <span className="inline-flex items-center gap-1.5 text-xs text-green-100/50">
              Made with
              <Heart
                size={13}
                className="fill-rose-400 text-rose-400"
              />
              for education
            </span>
          </div>
        </motion.div>
      </div>
    </footer>
  );
}